import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'carbon-react/lib/components/button';

import { ROUTES, getForecastByIdOnboardingUrl } from '@futrli/shared-services';

import { OnboardingPageTitle } from '../components/onboarding-page-title';
import * as styles from '../forecasting-onboarding.scss';
import { OnboardingPredictionsRouter } from '../components/onboarding-predictions-router';
import {
  BUDGET_ONBOARDING_STEP,
  LS_FORECASTING_ONBOARDING_STEP,
  SCENARIO_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../const';
import { ForecastType } from './onboarding-type-select';
import { useOnboardingUrlInfo } from '../utils/hooks';
import { usePredictionsInfoContext } from '../../detail/context/predictions-info-context';

export const OnboardingCustomise = () => {
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisatioId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisatioId || '';
  const { forecast, turnOffPredictionsExplanations } = usePredictionsInfoContext();

  const navigate = useNavigate();

  useEffect(() => {
    turnOffPredictionsExplanations?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    isOnboardingBudgetRoute,
    forecastType,
    isOnboardingScenarioRoute,
    isOnboardingScenarioWithInvoicesRoute
  } = useOnboardingUrlInfo();

  const handleNextClick = () => {
    if (!forecast) {
      return;
    }

    let nextStep;

    if (isOnboardingBudgetRoute) {
      nextStep =
        forecastType === ForecastType.FromScratch
          ? BUDGET_ONBOARDING_STEP.SCRATCH_ADDITIONAL_CONTROLS
          : forecastType === ForecastType.FromLYA
          ? BUDGET_ONBOARDING_STEP.LYA_ADDITIONAL_CONTROLS
          : BUDGET_ONBOARDING_STEP.AUTO_PREDICTIONS_ADDITIONAL_CONTROLS;
    } else if (isOnboardingScenarioRoute) {
      nextStep =
        forecastType === ForecastType.FromScratch
          ? SCENARIO_ONBOARDING_STEP.SCRATCH_CONTROL_CASH_FLOW
          : forecastType === ForecastType.WithAutoPredictions
          ? SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_CONTROL_CASH_FLOW
          : SCENARIO_ONBOARDING_STEP.LYA_CONTROL_CASH_FLOW;
    } else {
      nextStep =
        forecastType === ForecastType.FromScratch
          ? SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_CONTROL_CASH_FLOW
          : forecastType === ForecastType.WithAutoPredictions
          ? SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_CONTROL_CASH_FLOW
          : SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_CONTROL_CASH_FLOW;
    }

    navigate(
      `${getForecastByIdOnboardingUrl(
        workspaceId,
        organisationId,
        forecast.id,
        isOnboardingBudgetRoute,
        isOnboardingScenarioWithInvoicesRoute
      )}/${nextStep}${ROUTES.SALES}${location.search}`
    );
    localStorage.setItem(LS_FORECASTING_ONBOARDING_STEP, nextStep);
  };

  return (
    <div className={styles.contentContainer} style={{ position: 'relative' }}>
      <OnboardingPageTitle
        title={
          <FormattedMessage
            id="onboarding.customise_with_your_predictions_here"
            defaultMessage="Customise with your own predictions here..."
          />
        }
        description={
          <FormattedMessage
            id="onboarding.your_forecast_is_nearly_ready"
            defaultMessage="Your budget or forecast is nearly ready for you <b>to add what you know</b>."
            values={{
              b: (chunks: string) => <span style={{ fontWeight: 500 }}>{chunks}</span>
            }}
          />
        }
      />
      <Button buttonType="primary" size="large" mb="30px" onClick={handleNextClick}>
        <FormattedMessage id="common.next" defaultMessage="Next" />
      </Button>
      <OnboardingPredictionsRouter />
    </div>
  );
};
